// Today's paper's board, as the page reads it: who this browser is, the name it plays under, and
// the edge's ranking (functions/board). Plain `vite` serves no /board, so there every call rejects.

import { useQuery } from "@tanstack/react-query";
import { PLAYER_ID, type Board, type Submission } from "../../shared/game/board.ts";

export type { Board, BoardRow, Submission } from "../../shared/game/board.ts";
export { cleanName } from "../../shared/game/board.ts";

const PLAYER = "newspaper.hl:player:1";
const NAME = "newspaper.hl:name:1";
const TIMEOUT_MS = 10_000;
const POLL_MS = 30_000;

// kept for this tab when storage is blocked
let heldPlayer: string | null = null;
let heldName = "";

function freshId(): string {
  // randomUUID needs a secure context; getRandomValues does not
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** This browser's player: a random id, kept across visits. */
export function playerId(): string {
  try {
    const saved = localStorage.getItem(PLAYER);
    if (saved !== null && PLAYER_ID.test(saved)) return saved;
  } catch {
    // storage blocked
  }
  heldPlayer ??= freshId();
  try {
    localStorage.setItem(PLAYER, heldPlayer);
  } catch {
    // kept for this tab
  }
  return heldPlayer;
}

/** The name last played under, or "". */
export function savedName(): string {
  try {
    return localStorage.getItem(NAME) ?? heldName;
  } catch {
    return heldName;
  }
}

export function saveName(name: string): void {
  heldName = name.trim();
  try {
    localStorage.setItem(NAME, heldName);
  } catch {
    // kept for this tab
  }
}

type Answer = { ok: true; board: Board } | { ok: false; error: string };

async function ask(path: string, init: RequestInit = {}): Promise<Board> {
  const response = await fetch(path, { ...init, signal: AbortSignal.timeout(TIMEOUT_MS) });
  // without the edge, /board is the page itself or a 404
  if (!(response.headers.get("content-type") ?? "").includes("application/json")) throw new Error(`board: ${response.status}`);
  const answer = (await response.json()) as Answer | null;
  if (answer?.ok !== true) throw new Error(answer?.error ?? `board: ${response.status}`);
  return answer.board;
}

/** Sends today's (or yesterday's) paper. The edge scores it; the first one per day counts. */
export function submitScore(day: number, submission: Submission): Promise<Board> {
  return ask(`/board/${day}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(submission) });
}

/** The day's board, with this browser's line when it has played. */
export function fetchBoard(day: number): Promise<Board> {
  return ask(`/board/${day}?player=${encodeURIComponent(playerId())}`);
}

export const boardKey = (day: number) => ["board", day] as const;

export function useBoard(day: number, enabled: boolean) {
  return useQuery({ queryKey: boardKey(day), queryFn: () => fetchBoard(day), enabled, refetchInterval: POLL_MS, staleTime: POLL_MS / 2 });
}
